/**
 * ApiKeyDisplayModal Component
 *
 * Modal for displaying a generated API key (shown only once).
 * Includes a warning that the key won't be shown again and copy-to-clipboard.
 *
 * Uses `useTranslation('modules.modals.apiKeyDisplay')` for labels.
 *
 * @example
 * ```tsx
 * <ApiKeyDisplayModal
 *   open={!!generatedKey}
 *   apiKey={{ key: 'sk_live_...', name: 'My Key', keyPrefix: 'sk_live', rateLimitPerMin: 1000, createdAt: Temporal.PlainDate.from('2025-01-01') }}
 *   onClose={() => setGeneratedKey(null)}
 * />
 * ```
 */
import * as React from 'react';
import { useState } from 'react';
import { type TestIdProps } from '../../utils';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { Icon } from '../../components/icons/Icon';
import { useTranslation } from '../../providers/UIProvider';
import { useFormatShortDate } from '../../hooks/dates-hooks';
import { Temporal } from '@insio/dates';

export interface ApiKeyData {
  /** The full API key value */
  key: string;
  /** Human-readable name */
  name: string;
  /** Key prefix for identification */
  keyPrefix: string;
  /** Rate limit per minute */
  rateLimitPerMin: number;
  /** When the key was created — rendered as a date in the viewer's time zone. */
  createdAt: Temporal.PlainDate | Temporal.Instant;
}

export interface ApiKeyDisplayModalProps extends TestIdProps {
  /** Whether the modal is visible */
  open: boolean;
  /** The generated API key data */
  apiKey: ApiKeyData;
  /** Called when the modal is dismissed */
  onClose: () => void;
}

export const ApiKeyDisplayModal: React.FC<ApiKeyDisplayModalProps> = ({
  open,
  apiKey,
  onClose
}) => {
  const t = useTranslation('modules.modals.apiKeyDisplay');
  const formatShortDate = useFormatShortDate();
  const [copied, setCopied] = useState(false);

  const handleCopyKey = () => {
    navigator.clipboard.writeText(apiKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal open={open} onClose={onClose} size='md' className='w-[90vw] sm:w-auto'>
      <Modal.Header onClose={onClose} label={t('title')} />
      <Modal.Content className='w-full max-h-[80vh] overflow-y-auto'>
        {/* Warning Box */}
        <div className='bg-warning-bg border border-warning-border rounded-lg p-md space-y-sm'>
          <div className='flex gap-sm mb-sm'>
            <Icon name='triangle-exclamation' size='md' />
            <p className='text-sm font-semibold text-warning-text'>{t('warningTitle')}</p>
          </div>
          <p className='text-xs text-warning-text'>{t('warningMessage')}</p>
        </div>

        {/* Key Display */}
        <div>
          <label className='text-xs font-semibold text-foreground-muted mb-sm block'>
            {t('yourKey')}
          </label>
          <div className='bg-background-input border border-border-base rounded-lg p-md font-mono text-sm break-all'>
            {apiKey.key}
          </div>
        </div>

        {/* Key Details */}
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-md text-sm'>
          <div>
            <p className='text-xs text-foreground-muted'>{t('fields.name')}</p>
            <p className='font-semibold text-foreground'>{apiKey.name}</p>
          </div>
          <div>
            <p className='text-xs text-foreground-muted'>{t('fields.prefix')}</p>
            <p className='font-mono text-foreground'>{apiKey.keyPrefix}</p>
          </div>
          <div>
            <p className='text-xs text-foreground-muted'>{t('fields.rateLimit')}</p>
            <p className='font-semibold text-foreground'>{apiKey.rateLimitPerMin}/min</p>
          </div>
          <div>
            <p className='text-xs text-foreground-muted'>{t('fields.createdAt')}</p>
            <p className='font-semibold text-foreground'>{formatShortDate(apiKey.createdAt)}</p>
          </div>
        </div>

        <div className='flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-md pt-md'>
          <Button variant='default' onClick={onClose} className='w-full sm:w-auto'>
            {t('close')}
          </Button>
          <Button variant='primary' onClick={handleCopyKey} className='w-full sm:w-auto'>
            {copied ? (
              <>
                <Icon name='check' size='sm' />
                {t('copied')}
              </>
            ) : (
              <>
                <Icon name='copy' size='sm' />
                {t('copyKey')}
              </>
            )}
          </Button>
        </div>
      </Modal.Content>
    </Modal>
  );
};

ApiKeyDisplayModal.displayName = 'ApiKeyDisplayModal';
